import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import '../pages/Pages.css';
import { newsService } from '../services/newsService';
import PlayerComparison from '../components/PlayerComparison';
import FormattedContentDisplay from '../components/FormattedContentDisplay';
import { formatDateEST } from '../utils/timezoneUtils';

function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticleAndRelated();
  }, [id]);

  const fetchArticleAndRelated = async () => {
    try {
      setLoading(true);
      const result = await newsService.getNewsById(id);
      if (result.success) {
        setArticle(result.news);
        
        const relatedResult = await newsService.getPublishedNews(5);
        if (relatedResult.success) {
          const filtered = relatedResult.news.filter(a => a.id !== id).slice(0, 4);
          setRelatedArticles(filtered);
        }
      } else {
        setError('Article not found');
      }
    } catch (err) {
      setError('Failed to load article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getFirstImage = () => {
    if (article?.header_image) {
      return article.header_image;
    }

    if (!article?.content) return null;
    
    try {
      const parsed = JSON.parse(article.content);
      if (Array.isArray(parsed)) {
        const imageBlock = parsed.find(b => b.type === 'image' && b.src);
        if (imageBlock?.src) {
          return imageBlock.src;
        }
      }
    } catch (e) {
      const imgMatch = article.content.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch) return imgMatch[1];
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="page">
        <h1>News</h1>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="page">
        <h1>News</h1>
        <p style={{ color: '#CC0000', fontSize: '1.1rem', margin: '2rem 0' }}>{error || 'Article not found'}</p>
        <Link to="/news" className="back-to-news-link">
          ← Back to News
        </Link>
      </div>
    );
  }

  return (
    <div className="page news-detail-page">
      <Link to="/news" className="back-to-news-link">
        ← Back to News
      </Link>

      <div className="news-detail-container">
        <div className="news-detail-main">
          {getFirstImage() && (
            <div className="article-featured-image">
              <img src={getFirstImage()} alt={article.title} />
            </div>
          )}

          <div className="article-content-wrapper">
            <div className="article-header">
              <h1 className="article-title">{article.title}</h1>
              <div className="article-meta">
                <span className="author">✍️ Written By: {article.created_by_name || article.author_name}</span>
                {article.published_by_name && (
                  <>
                    <span className="divider">•</span>
                    <span className="publisher">📤 Published By: {article.published_by_name}</span>
                  </>
                )}
                <span className="divider">•</span>
                <span className="date">{formatDateEST(article.published_at)}</span>
              </div>
            </div>

            {article.media_type === 'comparison' && article.player1_name && article.player2_name && (
              <div className="article-comparison-section">
                <PlayerComparison 
                  player1Name={article.player1_name}
                  player2Name={article.player2_name}
                />
              </div>
            )}

            <div className="article-body">
              <FormattedContentDisplay content={article.content} />
            </div>

            <div className="article-footer">
            </div>
          </div>
        </div>

        <div className="news-detail-sidebar">
          <div className="sidebar-section">
            <h3>Latest News</h3>
            <div className="related-articles">
              {relatedArticles.map((article) => (
                <Link 
                  key={article.id} 
                  to={`/news/${article.id}`}
                  className="related-article-item"
                >
                  <div className="related-article-title">{article.title}</div>
                  <div className="related-article-date">
                    {formatDateEST(article.published_at)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewsDetail;
